import { Address } from 'viem';
import type { WriteContractReturnType } from 'viem/actions';
import type { SendTransactionReturnType } from 'viem/actions';
import type { Config } from 'wagmi';
import type { Chain } from 'viem/chains';

/**
 * Wallet Service Interface
 * Abstract layer for wallet interactions (approve, sendTransaction)
 * This allows easy scaling to different wallet providers
 */
export interface IWalletService {
  /**
   * Approve token spending
   */
  approveToken(params: ApproveTokenParams): Promise<WriteContractReturnType>;

  /**
   * Send a transaction
   */
  sendTransaction(params: SendTransactionParams): Promise<SendTransactionReturnType>;

  /**
   * Check if token is already approved
   */
  checkAllowance(params: CheckAllowanceParams): Promise<bigint>;

  /**
   * Switch chain if needed
   */
  switchChain(chainId: number): Promise<Chain>;
}

/**
 * Parameters for approve token
 */
export interface ApproveTokenParams {
  tokenAddress: Address;
  spenderAddress: Address;
  amount: bigint;
  account: Address;
  chain: Chain;
}

/**
 * Parameters for send transaction
 */
export interface SendTransactionParams {
  to: Address;
  value?: bigint;
  data?: `0x${string}`;
  gas?: bigint;
  account: Address;
  chain: Chain;
}

/**
 * Parameters for checking allowance
 */
export interface CheckAllowanceParams {
  tokenAddress: Address;
  ownerAddress: Address;
  spenderAddress: Address;
  chainId: number;
}

/**
 * ERC20 ABI for token interactions
 */
const ERC20_ABI = [
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: '_spender', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'allowance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: '_owner', type: 'address' },
      { name: '_spender', type: 'address' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

/**
 * Wagmi-based Wallet Service Implementation
 * Works with any wagmi-compatible wallet (MetaMask, WalletConnect, Coinbase, etc.)
 */
export class WagmiWalletService implements IWalletService {
  private writeContractAsync: any;
  private sendTransactionAsync: any;
  private switchChainAsync?: (params: { chainId: number }) => Promise<Chain>;
  private publicClient: any;
  private config: Config;

  constructor(
    writeContractAsync: any,
    sendTransactionAsync: any,
    publicClient: any,
    config: Config,
    switchChainAsync?: (params: { chainId: number }) => Promise<Chain>
  ) {
    this.writeContractAsync = writeContractAsync;
    this.sendTransactionAsync = sendTransactionAsync;
    this.publicClient = publicClient;
    this.config = config;
    this.switchChainAsync = switchChainAsync;
  }

  /**
   * Approve token spending
   */
  async approveToken(params: ApproveTokenParams): Promise<WriteContractReturnType> {
    const { tokenAddress, spenderAddress, amount, account, chain } = params;

    const hash = await this.writeContractAsync({
      address: tokenAddress,
      abi: ERC20_ABI,
      functionName: 'approve',
      args: [spenderAddress, amount],
      account,
      chain,
    });

    return hash;
  }

  /**
   * Send a transaction
   */
  async sendTransaction(params: SendTransactionParams): Promise<SendTransactionReturnType> {
    const { to, value = BigInt(0), data, gas, account, chain } = params;

    const hash = await this.sendTransactionAsync({
      account,
      to,
      value,
      data,
      gas,
      chain,
    });

    return hash;
  }

  /**
   * Check if token is already approved
   */
  async checkAllowance(params: CheckAllowanceParams): Promise<bigint> {
    const { tokenAddress, ownerAddress, spenderAddress, chainId } = params;

    try {
      const chain = this.config.chains.find(c => c.id === chainId);
      if (!chain) {
        throw new Error(`Chain ${chainId} not found`);
      }

      const allowance = await this.publicClient.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [ownerAddress, spenderAddress],
        chain,
      });

      return allowance as bigint;
    } catch (error) {
      return BigInt(0);
    }
  }

  /**
   * Switch chain if needed
   */
  async switchChain(chainId: number): Promise<Chain> {
    if (!this.switchChainAsync) {
      throw new Error('Switch chain not available');
    }

    try {
      const chain = await this.switchChainAsync({ chainId });
      return chain;
    } catch (error) {
      throw error;
    }
  }
}

/**
 * Factory function to create wallet service from wagmi hooks
 */
export const createWagmiWalletService = (
  writeContractAsync: any,
  sendTransactionAsync: any,
  publicClient: any,
  config: Config,
  switchChainAsync?: (params: { chainId: number }) => Promise<Chain>
): IWalletService => {
  return new WagmiWalletService(
    writeContractAsync,
    sendTransactionAsync,
    publicClient,
    config,
    switchChainAsync
  );
};

